// src/components/layouts/AuthLayout.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { Logo } from './ui/Logo';
import MY_CONFIG from '@/constants';
import { Link } from 'react-router-dom';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ 
  children, 
  title, 
  subtitle 
}) => {
  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-[#000000] dark:via-[#0A0A0A] dark:to-[#1C1C1E] transition-colors">
      
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10"
          animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10"
          animate={{ x: [0, -100, 0], y: [0, 50, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl relative z-10">
        <div className="bg-white/80 dark:bg-[#1C1C1E]/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800/50 p-8 transition-all">
         <div className="flex">
           <Link to="/" className="
                  flex items-center gap-2
                  bg-black dark:bg-white
                  text-white dark:text-black
                  px-4 py-2.5
                  rounded-2xl
                  font-medium
                  shadow-lg
                  hover:scale-105
                  transition
                "> 
          Home </Link>
         </div>
          <Logo imageUrl={MY_CONFIG.LOGO_URL} />
          
          <div className="text-center mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-purple-600 dark:from-gray-100 dark:to-purple-400 bg-clip-text text-transparent">
              {title}
            </h1>
            {subtitle && (
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                {subtitle}
              </p>
            )}
          </div>

          {children}
        </div>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-6 text-gray-500 dark:text-gray-500 text-xs flex items-center justify-center gap-1"
        >
          <Shield className="w-3 h-3" />
          Secure connection • End-to-end encrypted
        </motion.p>
      </div>
    </div>
  );
};